import React, { useContext, useEffect, useState, useMemo, useCallback } from 'react';
import Swal from 'sweetalert2';
import apiClient from '../../services/api/apiClient.js';
import { AuthContext } from '../../contexts/AuthContext.jsx';
import { Pencil, Ban, DollarSign } from 'lucide-react';
import DataTable from '../../components/common/DataTable.jsx';
import Pagination from '../../components/common/Pagination.jsx';
import Modal from '../../components/common/Modal.jsx';
import { formatMoney, formatDate, primero, today, daysAgo, useLocalTable } from '../finanzas-cxp/shared.js';
import { exportTablePdf, buildTablePdf, exportTableExcel } from '../../utils/exportUtils.js';
import EmailModal from '../../components/common/EmailModal.jsx';
import { enviarCorreoAdjunto, plantillaCorreoV1 } from '../../services/api/mail.js';

/**
 * "Aplicar Anticipo" — réplica fiel de `ListadoAnticipos.jsx#aplicarAnticipo`/
 * `savePago` de V1 (2026-09-28, instrucción explícita "hacer todo como V1" —
 * SUPERA el alcance reducido documentado antes en DUDAS.md D-11.10). Carga los
 * Cargos/CargosContenedor pendientes del cliente del anticipo, permite aplicar el
 * saldo disponible del anticipo contra N de ellos, y al guardar:
 *   1. Por cada renglón seleccionado: `POST /abonos/aplicar-anticipo`
 *      (formaPago `Anticipo <numero> Aplicado`) + `PATCH /cargos(-contenedor)/:id/pagar`.
 *   2. Al final: `PATCH /anticipos/aplicar-abono/:id` con `{saldo: total, utilizado: total}`
 *      (mismo nombre de campos confuso que V1 — `saldo` aquí es el MONTO aplicado,
 *      no el saldo absoluto restante; lo resta el backend).
 */
function ModalAplicarAnticipo({ anticipo, onClose, onAplicado }) {
  const [renglones, setRenglones] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const clienteId = primero(anticipo.clientes)?._id || primero(anticipo.clientes);
  const bancoId = primero(anticipo.bancos)?._id || primero(anticipo.bancos);
  const numero = anticipo.idAnticipo;
  const clienteNombre = primero(anticipo.clientes)?.nombre_comercial || '';

  useEffect(() => {
    Promise.all([
      apiClient.get(`/cargos/cliente/${clienteId}/por-pagar`),
      apiClient.get(`/cargos-contenedor/cliente/${clienteId}/por-pagar`),
    ]).then(([rC, rCC]) => {
      const c = (rC.data.data || []).map((a) => ({
        id: a._id, tipo: 'Cargo', fecha: a.fecha, nota: a.idCargo,
        total: a.total, comision: a.comisionNumero || 0, abonos: a.abonos, saldo: a.saldo,
        importe: a.saldo, observaciones: 'NA', select: false,
      }));
      const cc = (rCC.data.data || []).map((a) => ({
        id: a._id, tipo: 'CargoContenedor', fecha: a.fecha, nota: a.idCargoContenedor,
        total: a.total, comision: 0, abonos: a.abonos, saldo: a.saldo,
        importe: a.saldo, observaciones: 'NA', select: false,
      }));
      setRenglones([...cc, ...c]);
    }).finally(() => setCargando(false));
  }, [clienteId]);

  const total = useMemo(
    () => renglones.filter((r) => r.select).reduce((t, r) => t + (parseFloat(r.importe) || 0), 0),
    [renglones]
  );

  function actualizar(id, cambios) {
    setRenglones((prev) => prev.map((r) => (r.id === id ? { ...r, ...cambios } : r)));
  }

  function cambiarImporte(id, valor) {
    const r = renglones.find((x) => x.id === id);
    if (!r) return;
    const num = parseFloat(valor) || 0;
    if (num > r.saldo) return Swal.fire({ icon: 'error', title: 'El pago excede el saldo del cargo' });
    actualizar(id, { importe: num });
  }

  async function guardar() {
    if (total > Number(anticipo.saldo)) {
      return Swal.fire({ icon: 'error', title: 'El total a aplicar excede el saldo disponible del anticipo' });
    }
    const seleccionados = renglones.filter((r) => r.select && r.importe > 0);
    if (!seleccionados.length) return Swal.fire({ icon: 'warning', title: 'Selecciona al menos un cargo' });

    const confirmacion = await Swal.fire({
      title: 'Estas seguro?', text: 'Se registrará el abono', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Si, Enviar!',
    });
    if (!confirmacion.isConfirmed) return;

    setGuardando(true);
    try {
      for (const r of seleccionados) {
        const esContenedor = r.tipo === 'CargoContenedor';
        await apiClient.post('/abonos/aplicar-anticipo', {
          fecha: today(), importe: r.importe, clientes: clienteId,
          ...(esContenedor ? { cargosContenedor: [r.id] } : { cargos: [r.id] }),
          formaPago: `Anticipo ${numero} Aplicado`, bancos: bancoId, observaciones: r.observaciones,
        });
        await apiClient.patch(`/${esContenedor ? 'cargos-contenedor' : 'cargos'}/${r.id}/pagar`, { saldo: r.importe });
      }
      await apiClient.patch(`/anticipos/aplicar-abono/${anticipo._id}`, { saldo: total, utilizado: total });
      await Swal.fire('Good job!', 'Creado con exito', 'success');
      onAplicado();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'No se pudo aplicar el anticipo', text: error.message });
    } finally {
      setGuardando(false);
    }
  }

  return (
    <Modal open onClose={onClose} title={`Aplicar Anticipo #${numero} - Cliente ${clienteNombre}`} width="max-w-5xl"
      footer={(
        <>
          <button onClick={onClose} className="btn-outline">Cerrar</button>
          <button onClick={guardar} disabled={guardando} className="btn-primary">
            {guardando ? 'Registrando...' : 'Registrar'}
          </button>
        </>
      )}
    >
      <div className="flex justify-end mb-3 text-base font-semibold">
        Saldo <span className="ml-2 rounded-full bg-danger-500 px-3 py-1 text-white">{formatMoney(anticipo.saldo)}</span>
      </div>
      {cargando ? <p className="text-slate-400">Cargando cargos pendientes...</p> : (
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50 text-slate-600">
            <tr>
              <th className="px-2 py-1.5"></th>
              <th className="px-2 py-1.5 text-left">Fecha</th>
              <th className="px-2 py-1.5 text-left">Nota</th>
              <th className="px-2 py-1.5 text-right">Total</th>
              <th className="px-2 py-1.5 text-right">Comision</th>
              <th className="px-2 py-1.5 text-right">Abonos</th>
              <th className="px-2 py-1.5 text-right">Saldo</th>
              <th className="px-2 py-1.5 text-right">Importe</th>
              <th className="px-2 py-1.5 text-left">Observaciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {renglones.length === 0 && <tr><td colSpan={9} className="px-2 py-4 text-center text-slate-400">Sin cargos pendientes</td></tr>}
            {renglones.map((r) => (
              <tr key={r.id}>
                <td className="px-2 py-1.5"><input type="checkbox" checked={r.select} onChange={(e) => actualizar(r.id, { select: e.target.checked })} /></td>
                <td className="px-2 py-1.5">{formatDate(r.fecha)}</td>
                <td className="px-2 py-1.5">{r.nota}</td>
                <td className="px-2 py-1.5 text-right">{formatMoney(r.total)}</td>
                <td className="px-2 py-1.5 text-right">{formatMoney(r.comision)}</td>
                <td className="px-2 py-1.5 text-right">{formatMoney(r.abonos)}</td>
                <td className="px-2 py-1.5 text-right">{formatMoney(r.saldo)}</td>
                <td className="px-2 py-1.5 text-right">
                  <input type="number" min="0" step="any" value={r.importe}
                    onChange={(e) => cambiarImporte(r.id, e.target.value)}
                    className="input-base w-28 text-right py-1" />
                </td>
                <td className="px-2 py-1.5">
                  <input type="text" value={r.observaciones} onChange={(e) => actualizar(r.id, { observaciones: e.target.value })} className="input-base py-1" />
                </td>
              </tr>
            ))}
            <tr className="font-bold">
              <td colSpan={7} />
              <td className="px-2 py-1.5 text-right">Total</td>
              <td className="px-2 py-1.5">{formatMoney(total)}</td>
            </tr>
          </tbody>
        </table>
      )}
    </Modal>
  );
}

/**
 * Anticipos — réplica de V1 (`Admin/Anticipos/ListadoAnticipos.jsx` + `AnticiposCreate.jsx`).
 *
 * Listado: rango Fecha Inicio/Fin (default 30 días; incluye cancelados en rojo con la
 * leyenda CANCELADO, igual que V1), filtros Cliente y Saldo (Pendiente/Aplicado),
 * buscador (cliente/banco), orden, 50 por página, fila TOTAL y PDF/Excel. Columnas:
 * Fecha, Numero, Cliente, Banco, Importe, Utilizado, Saldo, Aplicar. Por fila: Editar y
 * Cancelar (solo si `utilizado == 0`) y Aplicar `$` (si `saldo > 0`).
 *
 * Formulario (alta y edición idénticos, igual que V1): Fecha; Cliente + Forma de Pago;
 * Observaciones; Importe + Tipo de Comision Cliente (+ % / $); Banco (solo MXN) + Tipo
 * de Comision Banco (+ % / $); "¿Lleva Comisión Vendedor?" Sí/No -> Proveedor Vendedor
 * + Tipo de Comision Vendedor (+ % / $). `Total` = % sobre el importe, `IVA` = % sobre
 * importe/1.16.
 *
 * Backend (campos PLANOS `tipo/porcentaje/numeroComision{Cliente,Banco,Vendedor}` +
 * `proveedorVendedor`, ver `anticipos.validator.js`): alta `POST /anticipos`, edición
 * `PATCH /anticipos/edit-complete/:id`, cancelar `PATCH /anticipos/cancelar/:id`.
 */
const TIPOS_COMISION = [
  { value: 'NA', label: 'Selecciona' },
  { value: 'Total', label: 'Total' },
  { value: 'IVA', label: 'IVA' },
];

function calcularNumero(tipo, importe, porcentaje) {
  const imp = Number(importe) || 0;
  const pct = Number(porcentaje) || 0;
  if (tipo === 'Total') return imp * (pct / 100);
  if (tipo === 'IVA') return Number(((imp / 1.16) * (pct / 100)).toFixed(2));
  return 0;
}
function calcularPorcentaje(tipo, importe, numero) {
  const imp = Number(importe) || 0;
  const num = Number(numero) || 0;
  if (tipo === 'Total') return imp ? Number(((num / imp) * 100).toFixed(2)) : 0;
  if (tipo === 'IVA') return imp ? Number(((num / (imp / 1.16)) * 100).toFixed(2)) : 0;
  return 0;
}

const vacio = () => ({
  fecha: today(),
  importe: 0, clientes: '', bancos: '', formaPago: 'Efectivo', observaciones: '',
  tipoComisionCliente: 'NA', porcentajeComisionCliente: 0, numeroComisionCliente: 0,
  tipoComisionBanco: 'NA', porcentajeComisionBanco: 0, numeroComisionBanco: 0,
  tipoComisionVendedor: 'NA', porcentajeComisionVendedor: 0, numeroComisionVendedor: 0,
  proveedorVendedor: '', tieneComisionVendedor: false,
});

/** Bloque "Tipo de Comision X" + (si aplica) "Comision X %" y "Comision X $". */
function ComisionCampos({ nombre, valores, set, ladoIzq }) {
  const tipoKey = `tipoComision${nombre}`;
  const pctKey = `porcentajeComision${nombre}`;
  const numKey = `numeroComision${nombre}`;
  return (
    <>
      {ladoIzq}
      <div>
        <label className="label">Tipo de Comision {nombre}</label>
        <select
          value={valores[tipoKey]}
          onChange={(e) => set({ [tipoKey]: e.target.value, [pctKey]: 0, [numKey]: 0 })}
          className="select-base"
        >
          {TIPOS_COMISION.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      </div>
      {valores[tipoKey] !== 'NA' && (
        <>
          <div>
            <label className="label">Comision {nombre} %</label>
            <input
              type="number" value={valores[pctKey]}
              onChange={(e) => set({ [pctKey]: e.target.value, [numKey]: calcularNumero(valores[tipoKey], valores.importe, e.target.value) })}
              className="input-base"
            />
          </div>
          <div>
            <label className="label">Comision {nombre} $</label>
            <input
              type="number" value={valores[numKey]}
              onChange={(e) => set({ [numKey]: e.target.value, [pctKey]: calcularPorcentaje(valores[tipoKey], valores.importe, e.target.value) })}
              className="input-base"
            />
          </div>
        </>
      )}
    </>
  );
}

export default function AnticiposPage() {
  const { hasPermiso } = useContext(AuthContext);
  const puedeVer = hasPermiso('anticipos:ver');
  const puedeCrear = hasPermiso('anticipos:crear');
  const puedeEditar = hasPermiso('anticipos:editar');

  const [formOpen, setFormOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [aplicando, setAplicando] = useState(null);
  const [valores, setValoresState] = useState(vacio);
  const [clientes, setClientes] = useState([]);
  const [bancos, setBancos] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [guardando, setGuardando] = useState(false);

  const [anticipos, setAnticipos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFechaInicio, setSelectedFechaInicio] = useState(daysAgo(30));
  const [selectedFechaFin, setSelectedFechaFin] = useState(today());
  const [selectedCliente, setSelectedCliente] = useState('');
  const [selectedSaldo, setSelectedSaldo] = useState('');
  const [mailOpen, setMailOpen] = useState(false);

  const set = (cambios) => setValoresState((v) => ({ ...v, ...cambios }));

  // Catálogos (clientes, bancos y proveedores tipo "Admin" para el vendedor) -- V1 los
  // carga al abrir el listado.
  useEffect(() => {
    apiClient.get('/clientes', { params: { limit: 500, sortBy: 'nombre_comercial', sortOrder: 'asc' } }).then(({ data }) => setClientes(data.data || [])).catch(() => setClientes([]));
    apiClient.get('/bancos', { params: { limit: 500, sortBy: 'banco', sortOrder: 'asc' } }).then(({ data }) => setBancos(data.data || [])).catch(() => setBancos([]));
    apiClient.get('/proveedores', { params: { limit: 500, tipo: 'Admin', sortBy: 'name', sortOrder: 'asc' } }).then(({ data }) => setProveedores(data.data || [])).catch(() => setProveedores([]));
  }, []);

  const cargar = useCallback(async () => {
    if (!selectedFechaInicio || !selectedFechaFin) return;
    setCargando(true);
    setError(null);
    try {
      const { data } = await apiClient.get(`/anticipos/fechas/${selectedFechaInicio}/${selectedFechaFin}`);
      setAnticipos((data.data || []).map((a) => {
        const cli = primero(a.clientes);
        const ban = primero(a.bancos);
        return {
          ...a,
          numero: a.idAnticipo, cliente: cli?.nombre_comercial || '—', idCliente: cli?._id,
          banco: ban ? `${ban.banco || ''} ${ban.cuenta || ''}`.trim() : '—', idBanco: ban?._id,
        };
      }));
    } catch (e) {
      setError(e.message);
      setAnticipos([]);
    } finally {
      setCargando(false);
    }
  }, [selectedFechaInicio, selectedFechaFin]);

  useEffect(() => { cargar(); }, [cargar]);

  const anticiposFiltrados = useMemo(
    () => anticipos.filter((a) =>
      (!selectedCliente || a.idCliente === selectedCliente) &&
      (selectedSaldo !== 'pendientes' || Number(a.saldo) !== 0) &&
      (selectedSaldo !== 'pagadas' || Number(a.saldo) === 0)),
    [anticipos, selectedCliente, selectedSaldo]
  );

  // V1 busca solo en cliente y banco; orden inicial por fecha desc.
  const { search, setSearch, page, setPage, sortBy, sortOrder, ordenarPor, pageRows, filtradas, pagination } = useLocalTable(anticiposFiltrados, {
    searchFields: ['cliente', 'banco'], perPage: 50, defaultSortBy: 'fecha', defaultSortOrder: 'desc',
  });

  function abrirNuevo() {
    setEditando(null);
    setValoresState(vacio());
    setFormOpen(true);
  }

  function abrirEditar(row) {
    setEditando(row);
    const tieneVend = !!(row.tipoComisionVendedor && row.tipoComisionVendedor !== 'NA' && Number(row.numeroComisionVendedor) > 0);
    const prov = Array.isArray(row.proveedorVendedor) ? row.proveedorVendedor[0] : row.proveedorVendedor;
    setValoresState({
      fecha: row.fecha, importe: row.importe, clientes: row.idCliente || '', bancos: row.idBanco || '',
      formaPago: row.formaPago || '', observaciones: row.observaciones || '',
      tipoComisionCliente: row.tipoComisionCliente || 'NA',
      porcentajeComisionCliente: row.porcentajeComisionCliente || 0,
      numeroComisionCliente: row.numeroComisionCliente || 0,
      tipoComisionBanco: row.tipoComisionBanco || 'NA',
      porcentajeComisionBanco: row.porcentajeComisionBanco || 0,
      numeroComisionBanco: row.numeroComisionBanco || 0,
      tipoComisionVendedor: row.tipoComisionVendedor || 'NA',
      porcentajeComisionVendedor: row.porcentajeComisionVendedor || 0,
      numeroComisionVendedor: row.numeroComisionVendedor || 0,
      proveedorVendedor: prov?._id || prov || '',
      tieneComisionVendedor: tieneVend,
    });
    setFormOpen(true);
  }

  // Al cambiar el importe se recalculan los 3 importes de comisión según su tipo (V1).
  function cambiarImporte(val) {
    set({
      importe: val,
      numeroComisionCliente: calcularNumero(valores.tipoComisionCliente, val, valores.porcentajeComisionCliente),
      numeroComisionBanco: calcularNumero(valores.tipoComisionBanco, val, valores.porcentajeComisionBanco),
      numeroComisionVendedor: calcularNumero(valores.tipoComisionVendedor, val, valores.porcentajeComisionVendedor),
    });
  }

  async function guardar(e) {
    e.preventDefault();
    if (!valores.clientes || !valores.bancos || !(Number(valores.importe) > 0) || !valores.fecha) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: 'Favor de completar todos los campos' });
      return;
    }
    if (editando) {
      const r = await Swal.fire({
        title: 'Estas seguro?', text: 'Se editará el Anticipo y se actualizarán todas las colecciones relacionadas',
        icon: 'warning', showCancelButton: true, confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Si, Editar!',
      });
      if (!r.isConfirmed) return;
    }
    const importe = Number(valores.importe) || 0;
    const numeroComisionCliente = Number(valores.numeroComisionCliente) || 0;
    const tiene = valores.tieneComisionVendedor;
    const payload = {
      fecha: valores.fecha, importe, formaPago: valores.formaPago, bancos: valores.bancos, clientes: valores.clientes,
      saldo: importe - numeroComisionCliente,
      tipoComisionCliente: valores.tipoComisionCliente,
      porcentajeComisionCliente: Number(valores.porcentajeComisionCliente) || 0,
      numeroComisionCliente,
      tipoComisionBanco: valores.tipoComisionBanco,
      porcentajeComisionBanco: Number(valores.porcentajeComisionBanco) || 0,
      numeroComisionBanco: Number(valores.numeroComisionBanco) || 0,
      // V1: al crear sin comisión de vendedor manda tipo "Total"/0/0; al editar manda "NA".
      tipoComisionVendedor: tiene ? valores.tipoComisionVendedor : (editando ? 'NA' : 'Total'),
      porcentajeComisionVendedor: tiene ? Number(valores.porcentajeComisionVendedor) || 0 : 0,
      numeroComisionVendedor: tiene ? Number(valores.numeroComisionVendedor) || 0 : 0,
      observaciones: valores.observaciones,
    };
    if (!editando) payload.utilizado = 0;
    // Sin vendedor, el backend usa su proveedor-vendedor por defecto (igual que V1).
    if (tiene && valores.proveedorVendedor) payload.proveedorVendedor = valores.proveedorVendedor;
    setGuardando(true);
    try {
      if (editando) await apiClient.patch(`/anticipos/edit-complete/${editando._id}`, payload);
      else await apiClient.post('/anticipos', payload);
      setFormOpen(false);
      await Swal.fire('Good job!', editando ? 'Anticipo editado con éxito' : 'Creado con exito', 'success');
      await cargar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: editando ? 'Error al editar' : 'Oops...', text: err.message });
    } finally {
      setGuardando(false);
    }
  }

  async function cancelar(row) {
    const confirm = await Swal.fire({
      title: 'Estas seguro?', text: 'Se cancelara el Anticipo!', icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#3085d6', cancelButtonColor: '#d33', confirmButtonText: 'Si, Cancelar!',
    });
    if (!confirm.isConfirmed) return;
    try {
      await apiClient.patch(`/anticipos/cancelar/${row._id}`);
      await Swal.fire('Good job!', 'Enviado con exito', 'success');
      await cargar();
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Oops...', text: err.message });
    }
  }

  // PDF/Excel de V1 (`ListadoAnticipos.jsx`): Fecha, Cliente, Importe (+ Total).
  const columnasExport = [
    { key: 'fecha', label: 'Fecha' },
    { key: 'cliente', label: 'Cliente' },
    { key: 'importe', label: 'Importe', render: (r) => formatMoney(r.importe) },
  ];
  const totalFiltrado = filtradas.reduce((t, r) => t + (Number(r.importe) || 0), 0);
  const opcionesPdf = { fileName: 'DetalleAnticipos', totalCols: { cliente: 'Total', importe: formatMoney(totalFiltrado) } };

  const columnas = [
    { key: 'fecha', label: 'Fecha', sortable: true, render: (r) => formatDate(r.fecha) },
    { key: 'numero', label: 'Numero', sortable: true, render: (r) => r.numero ?? '—' },
    { key: 'cliente', label: 'Cliente', sortable: true },
    { key: 'banco', label: 'Banco', sortable: true },
    { key: 'importe', label: 'Importe', sortable: true, className: 'text-right', render: (r) => formatMoney(r.importe) },
    { key: 'utilizado', label: 'Utilizado', sortable: true, className: 'text-right', render: (r) => formatMoney(r.utilizado) },
    { key: 'saldo', label: 'Saldo', sortable: true, className: 'text-right', render: (r) => formatMoney(r.saldo) },
    {
      key: '__acciones', label: 'Aplicar', sortable: false, className: 'text-center',
      render: (row) => (row.is_active === 'No' ? <span className="font-semibold">CANCELADO</span> : (
        <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
          {puedeEditar && Number(row.utilizado) === 0 && (
            <>
              <button title="Editar" aria-label="Editar" className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50" onClick={() => abrirEditar(row)}>
                <Pencil size={15} />
              </button>
              <button title="Cancelar" aria-label="Cancelar" className="p-1.5 rounded-md text-danger-500 hover:bg-slate-100" onClick={() => cancelar(row)}>
                <Ban size={15} />
              </button>
            </>
          )}
          {puedeEditar && Number(row.saldo) > 0 && (
            <button title="Aplicar" aria-label="Aplicar" className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50" onClick={() => setAplicando(row)}>
              <DollarSign size={15} />
            </button>
          )}
        </div>
      )),
    },
  ];

  const sum = (campo) => pageRows.reduce((t, r) => t + (Number(r[campo]) || 0), 0);

  if (!puedeVer) return <div className="text-slate-500">No tienes permiso para ver este módulo.</div>;

  const bancosMXN = [...bancos].sort((a, b) => (a.banco > b.banco ? 1 : -1)).filter((b) => b.moneda === 'MXN');

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-slate-900">Anticipos</h1>
        {puedeCrear && (
          <button onClick={abrirNuevo} className="btn-primary">Crear Anticipo</button>
        )}
      </div>

      <div className="flex items-center gap-3">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="input-base w-64" />
        <div className="flex items-center gap-2">
          <button
            type="button" className="btn-outline text-xs px-3 py-1.5"
            onClick={() => exportTablePdf('Detalle de Anticipos', columnasExport, filtradas, opcionesPdf)}
          >
            PDF
          </button>
          <button type="button" className="btn-outline text-xs px-3 py-1.5" onClick={() => setMailOpen(true)}>
            eMail
          </button>
          <button
            type="button" className="btn-outline text-xs px-3 py-1.5"
            onClick={() => exportTableExcel('Detalle de Anticipos', columnasExport, filtradas, {
              fileName: `DetalleAnticipos-${selectedFechaInicio}-${selectedFechaFin}`, sheetName: 'DetalleAnticipos',
            })}
          >
            Excel
          </button>
        </div>
        {error && <span className="text-sm text-danger-600">{error}</span>}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="label">Fecha Inicio</label>
          <input type="date" value={selectedFechaInicio} onChange={(e) => { setSelectedFechaInicio(e.target.value); setPage(1); }} className="input-base" />
        </div>
        <div>
          <label className="label">Fecha Fin</label>
          <input type="date" value={selectedFechaFin} onChange={(e) => { setSelectedFechaFin(e.target.value); setPage(1); }} className="input-base" />
        </div>
        <div>
          <label className="label">Cliente</label>
          <select value={selectedCliente} onChange={(e) => { setSelectedCliente(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            {clientes.map((c) => <option key={c._id} value={c._id}>{c.nombre_comercial}</option>)}
          </select>
        </div>
        <div>
          <label className="label">Saldo</label>
          <select value={selectedSaldo} onChange={(e) => { setSelectedSaldo(e.target.value); setPage(1); }} className="select-base">
            <option value="">Selecciona</option>
            <option value="pendientes">Pendiente</option>
            <option value="pagadas">Aplicado</option>
          </select>
        </div>
      </div>

      <DataTable
        columns={columnas} data={pageRows} loading={cargando} sortBy={sortBy} sortOrder={sortOrder} onSort={ordenarPor}
        footer={{ cliente: 'TOTAL', importe: formatMoney(sum('importe')), utilizado: formatMoney(sum('utilizado')), saldo: formatMoney(sum('saldo')) }}
      />
      <Pagination pagination={pagination} page={page} onChange={setPage} />

      <EmailModal
        open={mailOpen} onClose={() => setMailOpen(false)} titulo="Enviar Listado Anticipos"
        onEnviar={async (email) => {
          const doc = await buildTablePdf('Detalle de Anticipos', columnasExport, filtradas, opcionesPdf);
          await enviarCorreoAdjunto({
            subject: 'Lista de Anticipos', email, fileName: 'ListaAnticipos.pdf', docPdf: doc,
            textoHtml: plantillaCorreoV1('Adjunto encontraras la lista de Anticipos.'),
          });
        }}
      />

      <Modal
        open={formOpen} onClose={() => setFormOpen(false)}
        title={editando ? `Editar Anticipo ${editando.numero} - ${editando.cliente}` : 'Nuevo Anticipo'}
        width="max-w-3xl"
        footer={(
          <>
            <button type="button" onClick={() => setFormOpen(false)} className="btn-outline">Regresar</button>
            <button type="submit" form="form-anticipo" disabled={guardando} className="btn-primary">
              {guardando ? 'Guardando...' : 'Guardar'}
            </button>
          </>
        )}
      >
        <form id="form-anticipo" onSubmit={guardar} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="label">Fecha</label>
              <input type="date" required value={valores.fecha} onChange={(e) => set({ fecha: e.target.value })} className="input-base" />
            </div>
            <div />
            <div>
              <label className="label">Cliente</label>
              <select value={valores.clientes} onChange={(e) => set({ clientes: e.target.value })} className="select-base">
                <option value="">Selecciona</option>
                {clientes.map((c) => <option key={c._id} value={c._id}>{c.codigo} {c.nombre_comercial}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Forma de Pago</label>
              <select value={valores.formaPago} onChange={(e) => set({ formaPago: e.target.value })} className="select-base">
                <option value="">Selecciona una Forma de Pago</option>
                <option value="Efectivo">Efectivo</option>
                <option value="Transfer">Transfer</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="label">Observaciones</label>
              <input type="text" value={valores.observaciones} onChange={(e) => set({ observaciones: e.target.value })} className="input-base" />
            </div>

            <div>
              <label className="label">Importe</label>
              <input type="number" min="0" step="any" value={valores.importe} onChange={(e) => cambiarImporte(e.target.value)} className="input-base" />
            </div>
            <ComisionCampos nombre="Cliente" valores={valores} set={set} />
          </div>

          <hr className="border-slate-200" />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <ComisionCampos
              nombre="Banco" valores={valores} set={set}
              ladoIzq={(
                <div>
                  <label className="label">Banco</label>
                  <select value={valores.bancos} onChange={(e) => set({ bancos: e.target.value })} className="select-base">
                    <option value="">Selecciona un Banco</option>
                    {bancosMXN.map((b) => <option key={b._id} value={b._id}>{b.banco} {b.cuenta}</option>)}
                  </select>
                </div>
              )}
            />
          </div>

          <hr className="border-slate-200" />
          <div>
            <label className="label">¿Lleva Comisión Vendedor?</label>
            <div className="mt-2 flex gap-8">
              <label className="flex items-center gap-2 text-sm">
                <input type="radio" name="tieneComisionVendedor" checked={valores.tieneComisionVendedor === true} onChange={() => set({ tieneComisionVendedor: true })} />
                Sí
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="radio" name="tieneComisionVendedor" checked={valores.tieneComisionVendedor === false}
                  onChange={() => set({ tieneComisionVendedor: false, proveedorVendedor: '', tipoComisionVendedor: 'NA', porcentajeComisionVendedor: 0, numeroComisionVendedor: 0 })}
                />
                No
              </label>
            </div>
          </div>
          {valores.tieneComisionVendedor && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <ComisionCampos
                nombre="Vendedor" valores={valores} set={set}
                ladoIzq={(
                  <div>
                    <label className="label">Proveedor Vendedor</label>
                    <select value={valores.proveedorVendedor} onChange={(e) => set({ proveedorVendedor: e.target.value })} className="select-base">
                      <option value="">Selecciona un Proveedor Vendedor</option>
                      {[...proveedores].sort((a, b) => (a.name > b.name ? 1 : -1)).map((p) => <option key={p._id} value={p._id}>{p.name}</option>)}
                    </select>
                  </div>
                )}
              />
            </div>
          )}
        </form>
      </Modal>

      {aplicando && (
        <ModalAplicarAnticipo
          anticipo={aplicando}
          onClose={() => setAplicando(null)}
          onAplicado={() => { setAplicando(null); cargar(); }}
        />
      )}
    </div>
  );
}
